import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Typography
} from '@material-ui/core'
import React, { useEffect, useState } from 'react'

import {
  createPlannerQotQuestion,
  deletePlannerQotQuestion,
  fetchPlannerGradeGroup,
  fetchPlannerQotQuestions,
  updatePlannerQotQuestion
} from 'api'
import { showToast } from 'app/helpers'

const items = (d) =>
  d && Array.isArray(d.results) ? d.results : Array.isArray(d) ? d : []

const SECTIONS = [
  { key: 'setup', label: 'Environment & Setup' },
  { key: 'facilitation', label: 'Facilitation' }
]

/**
 * Per-grade preschool QOT editor — admin manages each grade's Yes/No questions
 * (the ones that appear when filling a preschool lesson observation).
 */
const QotSetup = () => {
  const [grades, setGrades] = useState([])
  const [gradeId, setGradeId] = useState('')
  const [questions, setQuestions] = useState([])
  const [loading, setLoading] = useState(false)
  const [dialog, setDialog] = useState(null)

  useEffect(() => {
    fetchPlannerGradeGroup({})
      .then((d) => {
        const gs = items(d)
        setGrades(gs)
        if (gs.length) setGradeId((cur) => cur || gs[0].uuid)
      })
      .catch(() => showToast('Failed to load grades', 'danger'))
  }, [])

  const load = async (gid) => {
    if (!gid) return
    setLoading(true)
    try {
      const d = await fetchPlannerQotQuestions({ queries: { grade_group: gid } })
      setQuestions(items(d))
    } catch (e) {
      showToast('Failed to load questions', 'danger')
    }
    setLoading(false)
  }

  useEffect(() => {
    load(gradeId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gradeId])

  const save = async () => {
    try {
      const payload = { ...dialog, grade_group: gradeId }
      if (dialog.uuid) await updatePlannerQotQuestion(payload)
      else await createPlannerQotQuestion(payload)
      setDialog(null)
      showToast('Saved', 'success')
      await load(gradeId)
    } catch (e) {
      showToast('Save failed', 'danger')
    }
  }

  const remove = async (q) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Delete this question?')) return
    try {
      await deletePlannerQotQuestion({ ids: [q.uuid] })
      showToast('Deleted', 'success')
      await load(gradeId)
    } catch (e) {
      showToast('Delete failed', 'danger')
    }
  }

  const bySection = (sec) =>
    questions
      .filter((q) => q.section === sec)
      .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))

  return (
    <Box mt={1}>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Each preschool grade has its own Quality Observation Template. Edits show
        up immediately in the observation form for that grade.
      </Typography>

      <Box display="flex" alignItems="center" style={{ gap: 12 }} mb={2} mt={1}>
        <FormControl variant="outlined" size="small" style={{ minWidth: 220 }}>
          <InputLabel>Grade</InputLabel>
          <Select
            label="Grade"
            value={gradeId}
            onChange={(e) => setGradeId(e.target.value)}
          >
            {grades.map((g) => (
              <MenuItem key={g.uuid} value={g.uuid}>
                {g.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {gradeId && (
          <Button
            size="small"
            variant="outlined"
            onClick={() =>
              setDialog({
                section: 'setup',
                title: '',
                description: '',
                is_active: true,
                display_order: questions.length + 1
              })
            }
          >
            + Add question
          </Button>
        )}
      </Box>

      {loading ? (
        <CircularProgress />
      ) : !gradeId ? (
        <Typography variant="body2" color="textSecondary">
          Pick a grade to manage its questions.
        </Typography>
      ) : (
        SECTIONS.map((s) => (
          <Box key={s.key} mb={2}>
            <Typography variant="subtitle1">{s.label}</Typography>
            {bySection(s.key).length === 0 && (
              <Typography variant="body2" color="textSecondary">
                No questions yet.
              </Typography>
            )}
            {bySection(s.key).map((q) => (
              <Paper
                key={q.uuid}
                variant="outlined"
                style={{ padding: 10, marginTop: 6, borderRadius: 8 }}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="flex-start"
                >
                  <Box pr={1}>
                    <Typography variant="body2" style={{ fontWeight: 600 }}>
                      {q.title}{' '}
                      {!q.is_active && <Chip size="small" label="inactive" />}
                    </Typography>
                    {q.description && (
                      <Typography variant="caption" color="textSecondary">
                        {q.description}
                      </Typography>
                    )}
                  </Box>
                  <Box style={{ whiteSpace: 'nowrap' }}>
                    <Button
                      size="small"
                      onClick={() =>
                        setDialog({
                          uuid: q.uuid,
                          section: q.section,
                          title: q.title,
                          description: q.description || '',
                          is_active: q.is_active,
                          display_order: q.display_order
                        })
                      }
                    >
                      Edit
                    </Button>
                    <Button
                      size="small"
                      style={{ color: '#9b2226' }}
                      onClick={() => remove(q)}
                    >
                      Del
                    </Button>
                  </Box>
                </Box>
              </Paper>
            ))}
          </Box>
        ))
      )}

      <Dialog
        open={!!dialog}
        onClose={() => setDialog(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {dialog && dialog.uuid ? 'Edit question' : 'Add question'}
        </DialogTitle>
        <DialogContent dividers>
          {dialog && (
            <Box display="flex" flexDirection="column" style={{ gap: 12 }}>
              <FormControl variant="outlined" size="small">
                <InputLabel>Section</InputLabel>
                <Select
                  label="Section"
                  value={dialog.section}
                  onChange={(e) =>
                    setDialog({ ...dialog, section: e.target.value })
                  }
                >
                  {SECTIONS.map((s) => (
                    <MenuItem key={s.key} value={s.key}>
                      {s.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Question"
                multiline
                minRows={2}
                value={dialog.title}
                onChange={(e) => setDialog({ ...dialog, title: e.target.value })}
              />
              <TextField
                label="Description (optional)"
                multiline
                minRows={2}
                value={dialog.description}
                onChange={(e) =>
                  setDialog({ ...dialog, description: e.target.value })
                }
              />
              <TextField
                label="Order"
                type="number"
                value={dialog.display_order}
                onChange={(e) =>
                  setDialog({ ...dialog, display_order: e.target.value })
                }
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={dialog.is_active !== false}
                    onChange={(e) =>
                      setDialog({ ...dialog, is_active: e.target.checked })
                    }
                  />
                }
                label="Active (shown in the observation form)"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Cancel</Button>
          <Button
            color="primary"
            variant="contained"
            onClick={save}
            disabled={!dialog || !dialog.title || !dialog.section}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default QotSetup
